import React from 'react';
import { FiLock, FiEye, FiEyeOff, FiCheckCircle, FiAlertCircle, FiArrowLeft } from 'react-icons/fi';
import { AuthPage, AuthCard } from './AuthLayout';
import useResetPassword from './useResetPassword';
import {
  Container, Header, Title, Subtitle, Form, FormGroup, InputWrapper, Input, IconWrapper,
  ToggleButton, Button, ErrorMessage, PasswordRequirements, Requirement, RequirementIcon,
  SuccessMessage, BackLink, LoadingSpinner
} from './ResetPassword.styles';

function ResetPassword() {
  const {
    navigate, formData, showPassword, setShowPassword, showConfirmPassword,
    setShowConfirmPassword, loading, error, success, token, passwordRequirements,
    isPasswordValid, handleInputChange, handleSubmit
  } = useResetPassword();

  if (success) {
    return (
      <AuthPage>
        <AuthCard
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Container>
            <Header>
              <Title>Password Reset Successful</Title>
              <Subtitle>Your password has been updated</Subtitle>
            </Header>

            <SuccessMessage>
              <FiCheckCircle size={20} />
              <div>
                <strong>Password reset successful!</strong><br />
                You will be redirected to the login page shortly.
              </div>
            </SuccessMessage>

            <Button onClick={() => {
              // Get preserved redirect from localStorage
              const preservedRedirect = localStorage.getItem('faithinsight_login_redirect');
              const loginUrl = preservedRedirect
                ? `/login?redirect=${encodeURIComponent(preservedRedirect)}`
                : '/login';
              navigate(loginUrl);
            }}>
              <FiArrowLeft size={16} />
              Go to Login
            </Button>
          </Container>
        </AuthCard>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <AuthCard
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Container>
          <BackLink onClick={() => {
            // Get preserved redirect from localStorage
            const preservedRedirect = localStorage.getItem('faithinsight_login_redirect');
            const loginUrl = preservedRedirect
              ? `/login?redirect=${encodeURIComponent(preservedRedirect)}`
              : '/login';
            navigate(loginUrl);
          }}>
            <FiArrowLeft size={16} />
            Back to Login
          </BackLink>

          <Header>
            <Title>Reset Password</Title>
            <Subtitle>Enter your new password below</Subtitle>
          </Header>

          <PasswordRequirements>
            <div style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.75rem', color: 'var(--text-primary)' }}>
              Password Requirements:
            </div>
            <Requirement className="info">
              <RequirementIcon valid={false}>•</RequirementIcon>
              At least 10 characters
            </Requirement>
            <Requirement className="info">
              <RequirementIcon valid={false}>•</RequirementIcon>
              At least one digit (0-9)
            </Requirement>
            <Requirement className="info">
              <RequirementIcon valid={false}>•</RequirementIcon>
              At least one special character (!@#$%^&*)
            </Requirement>
          </PasswordRequirements>

          <Form onSubmit={handleSubmit} style={{ marginTop: '1.5rem' }}>
            <FormGroup>
              <InputWrapper>
                <IconWrapper>
                  <FiLock size={16} />
                </IconWrapper>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Enter new password"
                  disabled={loading}
                  autoFocus
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </ToggleButton>
              </InputWrapper>
              {formData.password && (
                <PasswordRequirements>
                  <Requirement className={passwordRequirements.length ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.length}>✓</RequirementIcon>
                    At least 10 characters
                  </Requirement>
                  <Requirement className={passwordRequirements.digit ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.digit}>✓</RequirementIcon>
                    At least one digit
                  </Requirement>
                  <Requirement className={passwordRequirements.specialChar ? 'valid' : 'invalid'}>
                    <RequirementIcon valid={passwordRequirements.specialChar}>✓</RequirementIcon>
                    At least one special character
                  </Requirement>
                </PasswordRequirements>
              )}
            </FormGroup>

            <FormGroup>
              <InputWrapper>
                <IconWrapper>
                  <FiLock size={16} />
                </IconWrapper>
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  placeholder="Confirm new password"
                  disabled={loading}
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </ToggleButton>
              </InputWrapper>
            </FormGroup>

            {error && (
              <ErrorMessage>
                <FiAlertCircle size={14} />
                {error}
              </ErrorMessage>
            )}

            <Button type="submit" disabled={loading || !token || !isPasswordValid || formData.password !== formData.confirmPassword}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  Resetting Password...
                </>
              ) : (
                <>
                  <FiCheckCircle size={16} />
                  Reset Password
                </>
              )}
            </Button>
          </Form>
        </Container>
      </AuthCard>
    </AuthPage>
  );
}

export default ResetPassword;
